import { useEffect } from 'react';

import { getContent } from '../../../shared/content.js';
import { Blocks } from '../../components/RichText.js';
import { useHref, useLocale, useStrings } from '../../i18n.js';
import { track } from '../../lib/analytics.js';
import { AdSlot } from '../AdSlot.js';
import { CreateForm } from '../CreateForm.js';

/**
 * The homepage.
 *
 * The tool is the page. The headline, the promise and the whole creation card are above
 * the fold with no marketing splash in front of them: a visitor should understand the job
 * and be able to start it within about five seconds. Everything explanatory sits below,
 * where it can be read by the people who want it.
 */

export function Home() {
  const s = useStrings();
  const c = getContent(useLocale()).home;
  const href = useHref();

  useEffect(() => {
    track('landing_view', 'public');
  }, []);

  return (
    <>
      <section className="hero">
        <div className="hero__inner">
          <h1 className="hero__headline">{s.landing.heroHeadline}</h1>
          <p className="hero__support">{s.landing.heroSupport}</p>

          <div className="hero__grid">
            <CreateForm />

            <aside className="preview" aria-labelledby="preview-title">
              <h2 className="preview__title" id="preview-title">
                {s.landing.previewTitle}
              </h2>
              <div className="preview__card">
                <p className="eyebrow">{s.result.eyebrow}</p>
                <p className="preview__amount tabular">€300</p>
                <p className="preview__note">{s.landing.previewNote}</p>
              </div>
              <p className="preview__disclaimer">{s.landing.previewExampleNote}</p>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--tint" aria-labelledby="steps-title">
        <div className="section__inner">
          <h2 className="section__title" id="steps-title">
            {c.stepsHeading}
          </h2>
          <p className="section__lede">{c.stepsLede}</p>

          <ol className="steps">
            {c.steps.map((step, index) => (
              <li className="step" key={step.title}>
                <p className="step__number" aria-hidden="true">
                  {index + 1}
                </p>
                <h3 className="step__title">{step.title}</h3>
                <p className="step__body">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section" aria-labelledby="rule-title">
        <div className="section__inner section__inner--narrow prose">
          <h2 className="section__title" id="rule-title">
            {c.ruleHeading}
          </h2>
          <Blocks blocks={c.ruleBlocks} />
        </div>
      </section>

      <section className="section section--tint" aria-labelledby="uses-title">
        <div className="section__inner">
          <h2 className="section__title" id="uses-title">
            {c.usesHeading}
          </h2>
          <p className="section__lede">{c.usesLede}</p>

          <div className="cards">
            {c.useCards.map((card) => (
              <a className="use-card" href={href(card.href)} key={card.href}>
                <h3 className="use-card__title">{card.title}</h3>
                <p className="use-card__body">{card.body}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="why-title">
        <div className="section__inner section__inner--narrow prose">
          <h2 className="section__title" id="why-title">
            {c.whyHeading}
          </h2>
          <Blocks blocks={c.whyBlocks} />
        </div>
      </section>

      {/* Last thing on the page, six sections below the creation card. */}
      <AdSlot slot="home-footer" />
    </>
  );
}
